/**
 * El sonido de un mensaje nuevo en Conversaciones: dos notas cortas hechas con Web Audio, sin
 * archivos. El navegador no deja sonar nada hasta que la persona toca la página una vez, así que el
 * contexto se crea con el primer clic o tecla (`unlockChime`). Se puede apagar y queda guardado en
 * este navegador.
 */

const PREF_KEY = 'agora-conversaciones-sonido';
const LAST_KEY = 'agora-conversaciones-sonido-ultimo';
const GAP_MS = 2500;

let ctx: AudioContext | null = null;
let lastAt = 0;

export const chimeEnabled = (): boolean => {
  try {
    return window.localStorage.getItem(PREF_KEY) !== '0';
  } catch {
    return true;
  }
};

export const setChimeEnabled = (on: boolean) => {
  try {
    window.localStorage.setItem(PREF_KEY, on ? '1' : '0');
  } catch {
    // sin almacenamiento: vale hasta recargar
  }
};

const audioContext = (): AudioContext | null => {
  if (ctx) return ctx;
  const AC =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch {
    ctx = null;
  }
  return ctx;
};

/** Se llama desde un gesto de la persona: crea el contexto y lo despierta. */
export const unlockChime = () => {
  const ac = audioContext();
  if (ac && ac.state === 'suspended') void ac.resume().catch(() => undefined);
};

const note = (ac: AudioContext, freq: number, at: number, length: number, peak: number) => {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(peak, at + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + length);
  osc.connect(gain).connect(ac.destination);
  osc.start(at);
  osc.stop(at + length + 0.02);
};

/** Suena una vez por mensaje aunque el panel esté abierto en varias pestañas, y no más de una vez
 *  cada 2,5 s cuando llegan varios juntos. `force` lo usa la prueba del botón. */
export const playChime = (opts: { key?: string | number; force?: boolean } = {}) => {
  if (!opts.force && !chimeEnabled()) return;
  const now = Date.now();
  if (!opts.force && now - lastAt < GAP_MS) return;
  if (opts.key != null) {
    try {
      const k = String(opts.key);
      if (window.localStorage.getItem(LAST_KEY) === k) return;
      window.localStorage.setItem(LAST_KEY, k);
    } catch {
      // sin almacenamiento: cada pestaña suena por su cuenta
    }
  }
  const ac = audioContext();
  if (!ac || ac.state !== 'running') return;
  lastAt = now;
  const t = ac.currentTime + 0.01;
  note(ac, 880, t, 0.16, 0.12);
  note(ac, 1318.5, t + 0.11, 0.28, 0.1);
};
